import type { SyntheticEvent } from "react";
import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { useCallerAuthority } from "../lib/authority";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import {
  Button,
  Copyable,
  Inline,
  InlineError,
  KeyValue,
  Page,
  Section,
  Skeleton,
  Stack,
  Text,
  TextInput,
  useToast,
} from "../ui";

type Team = components["schemas"]["TeamResponse"];

export interface TeamDetailSettingsViewProps {
  canDelete: boolean;
  canRename: boolean;
  deleteErrorMessage?: string;
  displayName: string;
  errorMessage?: string;
  isDeletePending: boolean;
  isPending: boolean;
  isRenamePending: boolean;
  onDelete: () => void;
  onDisplayNameChange: (value: string) => void;
  onSubmitRename: () => void;
  renameErrorMessage?: string;
  saved: boolean;
  team?: Team;
}

// Team detail — `/settings/teams/:teamId` opens the team in place. It never
// switches org/team scope. Rename is authorized for a team owner or an org
// admin, delete stays owner-tier (org owner or team owner) — both are
// affordance gates only; `/v1` remains server-authoritative (docs/05 §6.4).
export function TeamDetailSettings() {
  const { teamId } = useParams({
    from: "/authenticated/billing-gate/app-shell/settings-shell/settings/teams/$teamId",
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [displayName, setDisplayName] = useState("");
  const [loadedTeamId, setLoadedTeamId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const authority = useCallerAuthority();

  const team = useQuery({
    queryKey: queryKeys.teams.detail(teamId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/teams/{id}", {
        params: { path: { id: teamId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  if (team.data && team.data.id !== loadedTeamId) {
    setDisplayName(team.data.display_name);
    setLoadedTeamId(team.data.id);
    setSaved(false);
  }

  const renameTeam = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.PATCH("/v1/teams/{id}", {
        body: { display_name: displayName.trim() },
        params: { path: { id: teamId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (updatedTeam) => {
      setDisplayName(updatedTeam.display_name);
      setSaved(true);
      toast.success("Team renamed");
      await queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
    },
    onError: (error) => {
      toast.error("Team rename failed", {
        description: requestErrorDescription(error, "Team request failed."),
      });
    },
  });

  const deleteTeam = useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.DELETE("/v1/teams/{id}", {
        params: { path: { id: teamId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      toast.success("Team deleted");
      await queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      void navigate({ to: "/settings/teams" });
    },
    onError: (error) => {
      toast.error("Team delete failed", {
        description: requestErrorDescription(error, "Team request failed."),
      });
    },
  });

  const latestTeam = renameTeam.data ?? team.data;
  // Membership-derivation seam (docs/05 §6.4): rename is authorized for a team
  // owner in THIS team or an org admin (org owner included via canManage);
  // delete stays owner-tier — org owner or a team owner in this team, never
  // widened to admin.
  const teamOwnerHere = authority.roleForTeam(teamId) === "owner";
  const canRename =
    authority.isOrgOwner || teamOwnerHere || authority.canManage;
  const canDelete = authority.isOrgOwner || teamOwnerHere;

  return (
    <TeamDetailSettingsView
      canDelete={canDelete}
      canRename={canRename}
      deleteErrorMessage={
        deleteTeam.isError ? deleteTeam.error.message : undefined
      }
      displayName={displayName}
      errorMessage={team.isError ? team.error.message : undefined}
      isDeletePending={deleteTeam.isPending}
      isPending={team.isPending}
      isRenamePending={renameTeam.isPending}
      onDelete={() => {
        deleteTeam.mutate();
      }}
      onDisplayNameChange={(value) => {
        renameTeam.reset();
        setSaved(false);
        setDisplayName(value);
      }}
      onSubmitRename={() => {
        renameTeam.mutate();
      }}
      renameErrorMessage={
        renameTeam.isError ? renameTeam.error.message : undefined
      }
      saved={saved}
      team={latestTeam}
    />
  );
}

export function TeamDetailSettingsView(props: TeamDetailSettingsViewProps) {
  return (
    <TeamDetailSettingsViewContent
      key={props.team?.id ?? "loading"}
      {...props}
    />
  );
}

function TeamDetailSettingsViewContent({
  canDelete,
  canRename,
  deleteErrorMessage,
  displayName,
  errorMessage,
  isDeletePending,
  isPending,
  isRenamePending,
  onDelete,
  onDisplayNameChange,
  onSubmitRename,
  renameErrorMessage,
  saved,
  team,
}: TeamDetailSettingsViewProps) {
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);
  const unchanged = team ? displayName.trim() === team.display_name : true;
  const saveDisabled =
    isRenamePending || displayName.trim().length === 0 || unchanged;
  const deleteDisabled =
    isDeletePending || !team || deleteConfirmation !== team.display_name;

  function handleRename(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSubmitRename();
  }

  return (
    <Page className="marker-team-detail" variant="config">
      {isPending ? (
        <Skeleton label="Loading team" rows={3} shape="detail-field" />
      ) : null}
      {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
      {!isPending && errorMessage == null && team ? (
        <Stack className="marker-team-detail__body" gap={6}>
          <Section>
            {canRename ? (
              <form onSubmit={handleRename}>
                <Stack gap={3}>
                  <Inline align="end" className="marker-field-row" gap={3}>
                    <TextInput
                      label="Display name"
                      required
                      value={displayName}
                      onChange={(event) => {
                        onDisplayNameChange(event.currentTarget.value);
                      }}
                    />
                    <Button disabled={saveDisabled} type="submit">
                      {isRenamePending ? "Saving..." : "Save"}
                    </Button>
                  </Inline>
                  {renameErrorMessage ? (
                    <InlineError>{renameErrorMessage}</InlineError>
                  ) : null}
                  {saved ? <Text role="status">Saved.</Text> : null}
                </Stack>
              </form>
            ) : (
              <Stack gap={2}>
                <KeyValue
                  rows={[{ label: "Display name", value: team.display_name }]}
                />
                <Text variant="muted">You can't rename this team.</Text>
              </Stack>
            )}
          </Section>

          <Section>
            <KeyValue
              rows={[
                {
                  label: "Team ID",
                  value: <Copyable label="Team ID" value={team.id} />,
                },
                { label: "Created", value: formatDateTime(team.created_at) },
                { label: "Updated", value: formatDateTime(team.updated_at) },
              ]}
            />
          </Section>

          {canDelete ? (
            <Section className="marker-team-detail__danger">
              <Stack gap={3}>
                {isDeleteConfirming ? (
                  <>
                    <Text variant="muted">
                      {`This hides ${team.display_name} from active team lists;` +
                        " its resources are retained. Type the team name to confirm."}
                    </Text>
                    <TextInput
                      label="Type the team name to confirm"
                      value={deleteConfirmation}
                      onChange={(event) => {
                        setDeleteConfirmation(event.currentTarget.value);
                      }}
                    />
                  </>
                ) : null}
                {deleteErrorMessage ? (
                  <InlineError>{deleteErrorMessage}</InlineError>
                ) : null}
                <Inline gap={3}>
                  {isDeleteConfirming ? (
                    <>
                      <Button
                        disabled={isDeletePending}
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setDeleteConfirmation("");
                          setIsDeleteConfirming(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={deleteDisabled}
                        type="button"
                        variant="danger"
                        onClick={onDelete}
                      >
                        {isDeletePending ? "Deleting..." : "Delete team"}
                      </Button>
                    </>
                  ) : (
                    <Button
                      disabled={isDeletePending}
                      type="button"
                      variant="danger"
                      onClick={() => {
                        setIsDeleteConfirming(true);
                      }}
                    >
                      Delete team
                    </Button>
                  )}
                </Inline>
              </Stack>
            </Section>
          ) : null}
        </Stack>
      ) : null}
    </Page>
  );
}
